import type { Character } from '@/types/screenplay';
import { createSentenceAudioBlocks, isLocalAudio, neutralizeSentenceAudioBlock, type SentenceAudioBlock } from './audioBlockMap';
import { BulletproofSequencer } from './bulletproofSequencer';
import { editorBlocksToSequence, rebuildCharactersFromSequence, screenplaySequence, sequenceToEditorBlocks } from './screenplayEditor';
import { requiredVoiceCharacters } from './voiceAssignments';
import { audioPreparationItems } from './audioPreparation';
import { estimateSpeechDurationSeconds } from './playbackBuffering';
import { prepareTextForSpeech } from './speechText';
import { canonicalJson } from './cachedPlayback';
import { cachedAudioPieces } from './cachedAudioBlocks';
import { withSequenceLanguage, type SpeechLanguage } from './speechLanguage';
import type { BlockSettings } from '../../supabase/functions/_shared/block-settings';

export interface CastingState {
  assignments: Record<string, string>;
  baseline_voices: Record<string, string>;
  /** First N estimated pages; absent on legacy full-screenplay plans. */
  page_limit?: number;
}
export const emptyCastingState = (): CastingState => ({ assignments: {}, baseline_voices: {} });
export function restoreCastingState(value: unknown, legacyAssignments: Record<string, string> = {}): CastingState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { assignments: legacyAssignments, baseline_voices: {} };
  const state = value as Record<string, unknown>;
  const record = (v: unknown): Record<string, string> => v && typeof v === 'object' && !Array.isArray(v)
    ? Object.fromEntries(Object.entries(v).filter((entry): entry is [string, string] => typeof entry[1] === 'string')) : {};
  return { assignments: record(state.assignments), baseline_voices: record(state.baseline_voices),
    ...(Number.isSafeInteger(state.page_limit) && Number(state.page_limit) > 0 ? { page_limit: Number(state.page_limit) } : {}) };
}
export function nextCastingState(characters: Character[], draft: Record<string, string>, previous: CastingState) {
  const roles = requiredVoiceCharacters(characters);
  const changed = roles.filter(role => draft[role] !== previous.assignments[role]);
  const assignments = Object.fromEntries(roles.map(role => [role, draft[role]]));
  const baseline_voices = Object.fromEntries(roles.filter(role => changed.includes(role) || previous.baseline_voices[role] === draft[role])
    .map(role => [role, draft[role]]));
  const state: CastingState = { assignments, baseline_voices,
    ...(previous.page_limit !== undefined ? { page_limit: previous.page_limit } : {}) };
  return { state, changed };
}

/** Same normalization as the editor, including exact saved delivery spans. */
export function castingSentenceBlocks(characters: Character[], assignments: Record<string, string>, language: SpeechLanguage = 'auto', settings: Partial<BlockSettings> = {}) {
  const sequence = editorBlocksToSequence(sequenceToEditorBlocks(screenplaySequence(characters)));
  return cachedAudioPieces(createSentenceAudioBlocks(BulletproofSequencer.createBulletproofSequence(
    withSequenceLanguage(rebuildCharactersFromSequence(sequence, characters), language), assignments)), settings);
}

export function withCharacterAnnouncements(sentences: SentenceAudioBlock[], enabled: boolean, narrator: string) {
  if (!enabled) return sentences;
  const seen = new Set<string>();
  return sentences.flatMap(sentence => {
    if (isLocalAudio(sentence) || sentence.type !== 'dialogue' || !sentence.character.trim() ||
      sentence.character === 'SCENE NARRATOR' || seen.has(sentence.sourceBlockId)) return [sentence];
    seen.add(sentence.sourceBlockId);
    const {audioSpeed:_dialogueSpeed,...announcement}=sentence;
    return [{ ...announcement, id: `announcement:${sentence.sourceBlockId}`, character: 'SCENE NARRATOR',
      text: prepareTextForSpeech(sentence.character, 'dialogue'), voiceId: narrator, synthesisOptions: Object.fromEntries(Object.entries(sentence.synthesisOptions).filter(([key]) => key === 'speech_language')), cacheSources: undefined }, sentence];
  });
}

/** These extras are prepared but NEVER inserted in screenplay playback/export.
 * Only roles explicitly confirmed for a new voice receive a neutral counterpart.
 * Persisting the role/voice pairs keeps unfinished baselines in subsequent plans. */
export function withCastingBaselines(sentences: SentenceAudioBlock[], baselineVoices: Record<string, string>) {
  const neutral = sentences.flatMap(s => {
    if (isLocalAudio(s) || baselineVoices[s.character] !== s.voiceId) return [];
    const baseline = neutralizeSentenceAudioBlock(s);
    return canonicalJson(baseline.synthesisOptions) === canonicalJson(s.synthesisOptions) ? [] : [{ ...baseline, id: `baseline:${s.id}` }];
  });
  return [...neutral, ...sentences];
}

export function castingPlan(sentences: SentenceAudioBlock[], revisions: ReadonlyMap<string, string>) {
  const spoken = sentences.filter(s => !isLocalAudio(s));
  const items = audioPreparationItems(spoken, revisions);
  if (items.length !== spoken.length) throw new Error('A voice is unavailable. Check the assignments again.');
  const unique = new Map<string, { sentence: SentenceAudioBlock; occurrences: number }>();
  for (const item of items) {
    const previous = unique.get(item.key);
    if (previous) previous.occurrences++;
    else unique.set(item.key, { sentence: item.sentence, occurrences: 1 });
  }
  if (unique.size > 10000 || spoken.length > 20000) throw new Error('This screenplay is too large for one audio preparation plan.');
  const entries = [...unique.values()];
  return { total: spoken.length, clips: entries.map(e => e.sentence), items: entries.map(({ sentence, occurrences }) => ({
    voice_id: sentence.voiceId, voice_revision: revisions.get(sentence.voiceId), text: sentence.text,
    synthesis_options: sentence.synthesisOptions, occurrences,
    ...(sentence.cacheSources && sentence.cacheSources.length > 1 ? { cache_sources: sentence.cacheSources.map(s => ({ text:s.text,synthesis_options:s.synthesisOptions })) } : {}),
  })) };
}
export function castingEstimate(clips: SentenceAudioBlock[], manifest: { cached: boolean }[]) {
  if (clips.length !== manifest.length) throw new Error('Incomplete audio estimate.');
  const missing = clips.filter((_, index) => !manifest[index].cached);
  const seconds = missing.reduce((sum, clip) => sum + estimateSpeechDurationSeconds(clip.text), 0);
  const modified = missing.filter(c => canonicalJson(c.synthesisOptions) !== canonicalJson(neutralizeSentenceAudioBlock(c).synthesisOptions)).length;
  return { minutes: seconds ? Math.ceil(seconds / 6) / 10 : 0, missing: missing.length,
    neutral: missing.length - modified, modified, reused: clips.length - missing.length };
}
